<template>
  <div id="app-shell">
    <header v-if="!isLanding" class="app-header" @click="goHome">
      <img src="./assets/icons/arrow.jpg" class="logo-icon" alt="logo">
      <span class="logo-text">Invest<span class="logo-text-1">Game</span></span>
    </header>

    <main class="app-main">
      <router-view v-slot="{ Component }">
        <transition name="fade">
          <component :is="Component" class="view-root" />
        </transition>
      </router-view>
    </main>
  </div>
</template>

<script>
export default {
  computed: {
    isLanding() {
      return process.env.VUE_APP_BUILD_TARGET === 'landing'
    }
  },
  methods: {
    goHome() {
      if (this.$route.path === '/') return
      this.$router.push('/')
    }
  }
}
</script>

<style>
:root {
  --color-text: #1a1a2e;
  --color-navy: #002060;
  --color-muted: #7F7F7F;
  --color-soft-muted: #555;
  --color-quiet: #666;
  --color-gray: #888;
  --color-light-gray: lightgray;
  --color-axis: #999;
  --color-white: #fff;
  --color-black: #000;
  --color-page: #fff;
  --color-accent: #AAC56F;
  --color-accent-hover: #96b35a;
  --color-market-blue: #64A9DC;
  --color-market-gray: #B9BDC7;
  --color-level-green: #B8D181;
  --color-level-blue: #8BBEE5;
  --color-level-gray: #BFBFBF;
  --color-level-gray-bg: #B8BDCC;
  --color-danger: #C00000;
  --color-primary: #2356A1;
  --color-card-title-start-blue: #5B8BD3;
  --color-card-title-start-gray: #939BB1;
  --color-card-title-start-green: #7B9D54;
  --color-button: #F1F2F6;
  --color-button-hover: #e3e5eb;
  --color-button-subtle-hover: #f5f5f5;
  --color-border: #CED1D8;
  --color-border-subtle: #eee;
  --color-logo-accent: #90bda2;
  --color-purple: #6600CC;
  --color-card-gray-alpha: rgba(185, 189, 199, 0.8);
  --color-card-blue-alpha: rgba(100, 169, 220, 0.8);
  --color-card-green-alpha: rgba(170, 197, 111, 0.8);
  --color-white-border: rgba(255, 255, 255, 0.7);
  --color-white-text-soft: rgba(255, 255, 255, 0.9);
  --color-navy-placeholder: rgba(0, 32, 96, 0.35);
  --color-navy-divider: rgba(0, 32, 96, 0.15);
  --surface-glass: rgba(255, 255, 255, 0.15);
  --surface-glass-strong: rgba(255, 255, 255, 0.85);
  --surface-row: rgba(255, 255, 255, 0.8);
  --surface-tabs: rgba(255, 255, 255, 0.7);
  --surface-chart: rgba(255, 255, 255, 0.6);
  --surface-footer: rgba(255, 255, 255, 0.9);
  --surface-dialog: rgba(241, 242, 246, 0.96);
  --surface-exit: rgba(241, 242, 246, 0.92);
  --surface-dialog-row: rgba(255, 255, 255, 0.5);
  --surface-exit-button: rgba(255, 255, 255, 0.6);
  --surface-exit-button-hover: rgba(255, 255, 255, 0.85);
  --overlay-exit: rgba(200, 200, 200, 0.35);
  --overlay-dialog: rgba(180, 180, 180, 0.4);
  --chart-fill-accent: rgba(170, 197, 111, 0.08);
  --gradient-card-title-blue: linear-gradient(to right, var(--color-card-title-start-blue), transparent);
  --gradient-card-title-gray: linear-gradient(to right, var(--color-card-title-start-gray), transparent);
  --gradient-card-title-green: linear-gradient(to right, var(--color-card-title-start-green), transparent);
  --gradient-result-title: linear-gradient(to right, rgba(0, 0, 0, 0.18), transparent);
  --gradient-level-title: linear-gradient(to right, var(--level-color), transparent);
  --shadow-soft: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 24px 0 rgba(0, 0, 0, 0.22);
  --shadow-soft-large: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 32px 0 rgba(0, 0, 0, 0.22);
  --shadow-strong: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 32px 0 rgba(0, 0, 0, 0.32);
  --shadow-compact: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 16px 0 rgba(0, 0, 0, 0.16);
  --shadow-exit: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 32px 0 rgba(0, 0, 0, 0.18);
  --shadow-exit-button: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 0 16px 0 rgba(0, 0, 0, 0.12);
  --shadow-card-min: 0 1px 4px rgba(0, 0, 0, 0.06);
  --shadow-field: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.08);
  --shadow-modal: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 8px 32px rgba(0, 0, 0, 0.18);

  --page-x: min(6.25dvw,10dvh);
  --space-2: min(0.15625dvw,0.25dvh);
  --space-3: min(0.234375dvw,0.375dvh);
  --space-4: min(0.3125dvw,0.5dvh);
  --space-5: min(0.390625dvw,0.625dvh);
  --space-6: min(0.46875dvw,0.75dvh);
  --space-8: min(0.625dvw,1dvh);
  --space-10: min(0.78125dvw,1.25dvh);
  --space-12: min(0.9375dvw,1.5dvh);
  --space-13: min(1.015625dvw,1.625dvh);
  --space-14: min(1.09375dvw,1.75dvh);
  --space-15: min(1.171875dvw,1.875dvh);
  --space-16: min(1.25dvw,2dvh);
  --space-18: min(1.40625dvw,2.25dvh);
  --space-20: min(1.5625dvw,2.5dvh);
  --space-22: min(1.71875dvw,2.75dvh);
  --space-24: min(1.875dvw,3dvh);
  --space-26: min(2.03125dvw,3.25dvh);
  --space-28: min(2.1875dvw,3.5dvh);
  --space-32: min(2.5dvw,4dvh);
  --space-34: min(2.65625dvw,4.25dvh);
  --space-44: min(3.4375dvw,5.5dvh);
  --space-48: min(3.75dvw,6dvh);
  --space-50: min(3.90625dvw,6.25dvh);
  --space-54: min(4.21875dvw,6.75dvh);
  --space-56: min(4.375dvw,7dvh);
  --space-60: min(4.6875dvw,7.5dvh);
  --space-62: min(4.84375dvw,7.75dvh);
  --space-64: min(5dvw,8dvh);
  --space-65: min(5.078125dvw,8.125dvh);
  --space-70: min(5.46875dvw,8.75dvh);
  --space-80: min(6.25dvw,10dvh);
  --space-100: min(7.8125dvw,12.5dvh);
  --space-110: min(8.59375dvw,13.75dvh);
  --space-120: min(9.375dvw,15dvh);
  --space-130: min(10.15625dvw,16.25dvh);
  --space-140: min(10.9375dvw,17.5dvh);
  --space-160: min(12.5dvw,20dvh);
  --space-180: min(14.0625dvw,22.5dvh);
  --space-200: min(15.625dvw,25dvh);
  --space-300: min(23.4375dvw,37.5dvh);
  --space-360: min(28.125dvw,45dvh);
  --space-500: min(39.0625dvw,62.5dvh);

  --fs-10: min(0.78125dvw,1.25dvh);
  --fs-11: min(0.859375dvw,1.375dvh);
  --fs-12: min(0.9375dvw,1.5dvh);
  --fs-13: min(1.015625dvw,1.625dvh);
  --fs-14: min(1.09375dvw,1.75dvh);
  --fs-15: min(1.171875dvw,1.875dvh);
  --fs-16: min(1.25dvw,2dvh);
  --fs-17: min(1.328125dvw,2.125dvh);
  --fs-18: min(1.40625dvw,2.25dvh);
  --fs-20: min(1.5625dvw,2.5dvh);
  --fs-22: min(1.71875dvw,2.75dvh);
  --fs-23: min(1.796875dvw,2.875dvh);
  --fs-24: min(1.875dvw,3dvh);
  --fs-25: min(1.953125dvw,3.125dvh);
  --fs-26: min(2.03125dvw,3.25dvh);
  --fs-28: min(2.1875dvw,3.5dvh);
  --fs-32: min(2.5dvw,4dvh);
  --fs-38: min(2.96875dvw,4.75dvh);
  --fs-40: min(3.125dvw,5dvh);
  --fs-46: min(3.59375dvw,5.75dvh);
  --fs-48: min(3.75dvw,6dvh);
  --fs-60: min(4.6875dvw,7.5dvh);
}

/* ─── Reset / Full-height no-scroll ─────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: var(--color-text);
  background: var(--color-page);
}

#app-shell {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─── Header ─────────────────────────────────────────────────── */
.app-header {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: var(--space-8) var(--space-20);
  background: var(--color-white);
  flex-shrink: 0;
  height: calc(var(--space-32) + var(--space-20));
  cursor: pointer;
}

.logo-icon {
  width: var(--space-32);
  height: var(--space-32);
  border-radius: var(--space-8);
  object-fit: cover;
}

.logo-text {
  font-size: var(--fs-17);
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: 0;
}

.logo-text-1 {
  color: var(--color-logo-accent);
}

/* ─── Main content ───────────────────────────────────────────── */
.app-main {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

.view-root {
  position: absolute;
  inset: 0;
}

/* ─── Page transitions ───────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ─── Typography helpers ─────────────────────────────────────── */
.font-46  { font-size: var(--fs-46); }
.font-40  { font-size: var(--fs-40); }
.font-32  { font-size: var(--fs-32); }
.font-28  { font-size: var(--fs-28); }
.font-24  { font-size: var(--fs-26); }
.font-20  { font-size: var(--fs-24); }
.font-20-table { font-size: var(--fs-20); }
.font-19  { font-size: var(--fs-23); }
.font-18  { font-size: var(--fs-22); }
.font-14  { font-size: var(--fs-18); }
.font-12  { font-size: var(--fs-14); }

/* SweetAlert above dialogs */
.swal2-container { z-index: 99999 !important; }

@media (max-width: 700px) {
  :root {
    --page-x: 20px;
    --space-2: 2px;
    --space-3: 3px;
    --space-4: 4px;
    --space-5: 5px;
    --space-6: 6px;
    --space-8: 8px;
    --space-10: 10px;
    --space-12: 12px;
    --space-13: 13px;
    --space-14: 14px;
    --space-15: 15px;
    --space-16: 16px;
    --space-18: 18px;
    --space-20: 20px;
    --space-22: 22px;
    --space-24: 24px;
    --space-26: 26px;
    --space-28: 28px;
    --space-32: 32px;
    --space-34: 34px;
    --space-44: 44px;
    --space-48: 48px;
    --space-50: 50px;
    --space-54: 54px;
    --space-56: 56px;
    --space-60: 60px;
    --space-62: 62px;
    --space-64: 64px;
    --space-65: 65px;
    --space-70: 70px;
    --space-80: 80px;
    --space-100: 100px;
    --space-110: 110px;
    --space-120: 120px;
    --space-130: 130px;
    --space-140: 140px;
    --space-160: 160px;
    --space-180: 180px;
    --space-200: 200px;
    --space-300: 300px;
    --space-360: 360px;
    --space-500: 500px;
  }

  .app-header {
    height: 48px;
    padding: 8px 16px;
  }

  .logo-icon {
    width: 30px;
    height: 30px;
  }

  .logo-text {
    font-size: 16px;
  }

  .font-46 { font-size: 28px; }
  .font-40 { font-size: 24px; }
  .font-32 { font-size: 22px; }
  .font-28 { font-size: 20px; }
  .font-24 { font-size: 18px; }
  .font-20 { font-size: 16px; }
  .font-18 { font-size: 16px; }
  .font-14 { font-size: 14px; }
}
@media (max-width: 1020px) {
  .font-20-table { font-size: 16px; }
  .font-12 { font-size: 10px; }
}
</style>
